import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { withTheme } from 'common/styling/theme';
import { ActionsButton, BasicModal, StatusCell } from 'common/components';
import { EditIcon } from 'common';
import StatementCard from '../StatementCard';
import {
  STATUSES,
  ALLOCATION_METHOD_BY_EQUITY,
  ALLOCATION_METHOD_FIXED,
  ALLOCATION_METHOD_EQUITY,
  ALLOCATION_METHOD_BALANCE,
} from '../../consts';
import { ratioToPercent } from '../../helpers';
import { staticStyles, getDynamicStyles } from './style';
import InvestEditRatioForm from '../InvestEditRatioForm';

const InvestStatementPanel = ({ investStatement, investmentId, theme }) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [editInvestStatement, setEditInvestStatement] = useState(false);

  const toggleModalEditStrategy = () => setEditInvestStatement(!editInvestStatement);

  const isMamMetaInvest = investStatement.strategy?.strategyType === 0 && investStatement.strategy?.isMeta;
  const shouldShowPercentCaption =
    investStatement.allocationMethod === ALLOCATION_METHOD_FIXED ||
    investStatement.allocationMethod === ALLOCATION_METHOD_BY_EQUITY;
  const ratioPercent = investStatement.ratio ? ratioToPercent(investStatement.ratio) : investStatement.ratio;
  const displayedRatio = shouldShowPercentCaption ? ratioPercent : investStatement.ratio;

  const allocationMethod = investStatement => {
    switch (investStatement.allocationMethod) {
      case ALLOCATION_METHOD_FIXED:
        return 'justFixed';
      case ALLOCATION_METHOD_EQUITY:
        return 'justEquity';
      case ALLOCATION_METHOD_BY_EQUITY:
        return 'justByEquity';
      case ALLOCATION_METHOD_BALANCE:
        return 'justBalanceRatio';
      default:
        return '';
    }
  };

  const strategyCells = [
    {
      captionId: 'justId',
      displayValue: investStatement.strategy.id,
    },
    {
      captionId: 'justName',
      displayValue: investStatement.strategy.title,
    },
  ];

  const investmentsCells = [
    {
      captionId: 'justMasterAccount',
      displayValue: investStatement.account.master_account,
    },
    {
      captionId: 'justCreated',
      displayValue: (
        <FormattedDate month="2-digit" day="2-digit" year="numeric" value={investStatement.account.created} />
      ),
    },
    {
      captionId: 'justCurrency',
      displayValue: investStatement.account.currency,
    },
    {
      captionId: 'justLeverage',
      displayValue: `1:${investStatement.account.leverage}`,
    },
    {
      captionId: 'justStatus',
      displayValue: <StatusCell statusCode={STATUSES[investStatement.account.status]} />,
    },
    ...(isMamMetaInvest
      ? [
          {
            captionId: 'justAllocationMethod',
            displayValue: (
              <>
                <FormattedMessage id={allocationMethod(investStatement)} />
                <ActionsButton className="InvestStatement__edit-button" watch onClickFunc={toggleModalEditStrategy}>
                  <EditIcon />
                </ActionsButton>
              </>
            ),
          },
          {
            captionId: shouldShowPercentCaption ? 'justPercent' : 'justRatio',
            displayValue: (
              <>
                {displayedRatio}
                {shouldShowPercentCaption ? '%' : ''}
                <ActionsButton className="InvestStatement__edit-button" watch onClickFunc={toggleModalEditStrategy}>
                  <EditIcon />
                </ActionsButton>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="InvestStatementPanel">
      <StatementCard cells={strategyCells} captionId="justStrategy" />
      <StatementCard cells={investmentsCells} captionId="justInvestment" />

      <BasicModal isOpen={editInvestStatement} onRequestClose={toggleModalEditStrategy} captionId="justEdit">
        <InvestEditRatioForm
          toggleModal={toggleModalEditStrategy}
          investmentId={investmentId}
          initialValues={{
            ratio: ratioPercent,
            allocationMethod: investStatement?.allocationMethod,
          }}
        />
      </BasicModal>

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

InvestStatementPanel.propTypes = {
  investStatement: PropTypes.object,
  investmentId: PropTypes.number.isRequired,
  theme: PropTypes.object,
};

InvestStatementPanel.defaultProps = {
  investStatement: {},
  theme: {},
};

export default withTheme()(InvestStatementPanel);
